import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	useLocation,
	useNavigate,
	useParams,
	useSearchParams,
} from "react-router-dom";
import {
	chatMessageDomId,
	persistedMessageDbId,
} from "../utils/contextGraphLink";
import {
	ChatContainer,
	ChatExportDropdown,
	ErrorDisplay,
	HeroSection,
} from "../components";
import {
	BlockType,
	type ColumnInspection,
	type ContentBlock,
	type LoanTapePreviewColumn,
	type TextBlock,
	type TraceBlock,
	TraceType,
	type SelectedVersions,
	type UIDocumentRef,
	type UIMessage,
} from "../components/Chat/Chat.types";
import {
	createAssistantMessage,
	createUserMessage,
	getApiHeaders,
	persistedMessageId,
	withLocalRegeneration,
	withPendingVersionResolved,
} from "../components/Chat/Chat.utils";
import MentionDropdown from "../components/MentionDropdown";
import SchemaExtractorForm, {
	type SchemaExtractorPayload,
} from "../components/SchemaExtractorForm";
import type {
	CatalogDocument,
	MentionTemplateOption,
} from "../hooks/useDocumentMention";
import TracePanel from "../components/TracePanel";
import ContextGraphAnswerView from "../components/ContextGraph/ContextGraphAnswerView";
import RailViewHeader from "../components/Chat/RailViewHeader";
import { useDopplerUriOpener } from "../hooks/useDopplerUriOpener";
import { CitationChainProvider } from "../components/CitationChain/CitationChainContext";
import { useAuth } from "../context/AuthContext";
import { PdfViewerProvider } from "../context/PdfViewerContext";
import { SpreadsheetViewerProvider } from "../context/SpreadsheetViewerContext";
import {
	EvidenceEventSchema,
	type EvidenceEvent,
} from "../types/evidence";
import {
	CHAT_HISTORY_UPDATED_EVENT,
	CHAT_STREAMING_STATUS_EVENT,
	type ChatHistoryEventDetail,
} from "../constants/events";
import { useDocumentMention } from "../hooks/useDocumentMention";
import { UIComponent } from "../types/uiComponent";
import { apiFetch } from "../utils/apiClient";
import { isSessionExpiredError } from "../utils/sessionExpiry";
import {
	chatDraftKey,
	ChatDraftSyncState,
	confirmChatDraftSubmission,
	migrateChatDraftSubmission,
	reconcileChatDraftSubmission,
	stageChatDraftSubmission,
	syncChatDraftForKey,
} from "../utils/chatDraft";
import {
	friendlyStatusMessage,
	publicSafeErrorMessage,
} from "../utils/apiErrorDetail";
import {
	TRACE_UI_ENABLED,
	isAuditTimelineEnabled,
	isLoanTapeTemplateEnabled,
} from "../utils/featureFlags";
import "./Chat.css";

// Templates surfaced in the @-mention dropdown (keyboard-navigable).
const MENTION_TEMPLATES: MentionTemplateOption[] = [
	{
		id: "schema-extractor",
		name: "Loan Tape Extraction",
		description:
			"Learn loan tape extraction from a raw → clean loan tape pair, then apply it to a new raw workbook",
		keywords: ["loan", "tape", "schema", "extract", "extractor", "template", "transform", "sql"],
	},
];

/** Text to land in the composer; a new nonce re-applies the same text. */
export interface ChatPrefill {
	text: string;
	nonce: number;
}

interface ChatProps {
	variant?: "default" | "side-panel";
	/**
	 * Embedded mode (DOP-440): the thread to show, `null` when there is none
	 * yet. When this prop is present the route's `:chatId` is ignored and a
	 * created chat is reported through `onChatCreated` instead of navigated to,
	 * so the page can live inside another page (the dashboard's Analyst rail).
	 */
	chatId?: string | null;
	onChatCreated?: (chatId: string) => void;
	/**
	 * A chat that could not be created: the status and the server's `detail`.
	 * The visible error stays the status's plain sentence (server text is not
	 * shown to users as-is); this lets the host render its own state for a
	 * refusal it understands, e.g. the harness being disabled.
	 */
	onCreateError?: (status: number, detail: string | null) => void;
	/** Extra fields for `POST /api/v3/chats` in embedded mode (arm, surface). */
	createChatBody?: Record<string, unknown>;
	prefill?: ChatPrefill | null;
	/** Extra `message_metadata` merged into every turn this page sends. */
	turnMetadata?: () => Record<string, unknown>;
	/**
	 * Documents every turn is about (the Deal Room file page's rail). Each
	 * turn carries them as `document_refs`, as if they were mentioned, without
	 * a chip in the composer: the rail itself says what it is beside.
	 */
	contextDocs?: CatalogDocument[];
	/**
	 * The one line of guidance above the composer in a side-panel thread that
	 * has no turns yet. Each rail names the thing it is beside — a rail whose
	 * empty state talks about a different surface is worse than no guidance —
	 * so the default is the dashboard's sentence and every other host passes
	 * its own. Ignored outside `variant="side-panel"`.
	 */
	sidePanelEmptyPrompt?: string;
}

const DEFAULT_SIDE_PANEL_EMPTY_PROMPT =
	"Ask about any number on this workflow: what it is, where it came from, or how it changed.";

type ChatHistoryMessage = {
	id: number;
	chat_id: string;
	role: string;
	content_blocks: ContentBlock[];
	message_metadata?: {
		document_refs?: Array<{
			doc_id: string;
			deal_name?: string | null;
			ref_type?: "document" | "deal" | null;
			project_id?: string | null;
			filename?: string | null;
		}>;
		streaming?: {
			generation_id: string;
			status: "pending" | "running";
			last_seq: number;
		} | null;
		evidence?: Record<string, unknown> | null;
		stream_error?: boolean | null;
		error_message?: string | null;
	};
	thread?: {
		live?: boolean;
		branch_of?: number | null;
		versions?: {
			question_message_id: number;
			index: number;
			total: number;
			ids: number[];
		} | null;
	};
};

type ChatHistoryResponse = {
	chat: {
		id: string;
		title: string;
		chat_type?: string | null;
		created_at: number;
		updated_at: number;
	};
	messages: ChatHistoryMessage[];
	next_cursor: string | null;
	has_more: boolean;
};

type ChatStreamStartResponse = {
	generation_id: string;
	assistant_message_id: number;
	initial_seq: number;
};

type StreamEnvelope = {
	generation_id: string;
	seq: number;
	event:
		| "text_delta"
		| "thinking_delta"
		| "block"
		| "evidence"
		| "done"
		| "error"
		| "cancelled";
	data: Record<string, unknown>;
};

const getBlockId = (block: ContentBlock): string | null => {
	if ("block_id" in block && typeof block.block_id === "string") {
		return block.block_id;
	}
	return null;
};

const getBlockRevision = (block: ContentBlock): number => {
	if ("revision" in block && typeof block.revision === "number") {
		return block.revision;
	}
	return 1;
};

const mergeContentBlock = (
	blocks: ContentBlock[],
	incoming: ContentBlock,
): ContentBlock[] => {
	const blockId = getBlockId(incoming);
	if (!blockId) return [...blocks, incoming];

	const index = blocks.findIndex((block) => getBlockId(block) === blockId);
	if (index < 0) return [...blocks, incoming];
	if (getBlockRevision(incoming) <= getBlockRevision(blocks[index])) {
		return blocks;
	}

	const next = [...blocks];
	next[index] = incoming;
	return next;
};

const CHAT_HISTORY_PAGE_SIZE = "20";
// Bound on a `?prefill=` draft. The dashboard's questions are a couple of
// sentences; anything longer arrived from a hand-edited URL, not from us.
const CHAT_PREFILL_MAX_LENGTH = 2000;
const STREAM_CANCEL_FALLBACK_TIMEOUT_MS = 5000;

/**
 * An answer's context graph, INSIDE the Analyst rail (DOP-492).
 *
 * A component rather than inline JSX so the citation opener resolves under the
 * viewer providers the page mounts in its own render — called at the top of
 * `Chat` it would sit outside them and every citation would degrade to a new
 * tab. `ContextGraphAnswerView` owns everything below the header (DOP-493).
 */
const RailGraphView: React.FC<{
	chatId: string;
	messageDbId: string;
	aboutLabel?: string;
	onBack: () => void;
}> = ({ chatId, messageDbId, aboutLabel, onBack }) => {
	const openHref = useDopplerUriOpener();
	return (
		<>
			<RailViewHeader title="Graph" aboutLabel={aboutLabel} onBack={onBack} />
			<div className="rail-view-body">
				<ContextGraphAnswerView
					chatId={chatId}
					messageId={messageDbId}
					onOpenHref={(href) => openHref(href, { chatId })}
				/>
			</div>
		</>
	);
};

// Module-level counter so stream tokens stay unique across remounts
let nextStreamToken = 0;

const Chat: React.FC<ChatProps> = ({
	variant = "default",
	chatId: chatIdProp,
	onChatCreated,
	onCreateError,
	createChatBody,
	prefill,
	turnMetadata,
	contextDocs,
	sidePanelEmptyPrompt = DEFAULT_SIDE_PANEL_EMPTY_PROMPT,
}) => {
	const { chatId: routeChatId } = useParams<{ chatId?: string }>();
	// Embedded: the host page owns the thread id, and the URL — the host's URL —
	// says nothing about this chat. Everything below keys off `chatIdParam`, so
	// this one substitution is what makes the whole page follow the prop.
	const embedded = chatIdProp !== undefined;
	const chatIdParam = embedded ? (chatIdProp ?? undefined) : routeChatId;
	const navigate = useNavigate();
	// Read at send time, not captured at render: the host's descriptor changes
	// as the user moves around the dashboard between turns.
	const turnMetadataRef = useRef(turnMetadata);
	useEffect(() => {
		turnMetadataRef.current = turnMetadata;
	});
	// Only used to namespace composer drafts (DOP-267).
	const { user } = useAuth();
	const [schemaFormOpen, setSchemaFormOpen] = useState(false);
	// Loan Tape is the only mention template today, so a tenant with the flag
	// off gets an empty list and the dropdown's template section disappears
	// along with it. Memoised because `useDocumentMention` holds the array.
	const loanTapeEnabled = isLoanTapeTemplateEnabled();
	const mentionTemplates = useMemo(
		() => (loanTapeEnabled ? MENTION_TEMPLATES : []),
		[loanTapeEnabled],
	);
	const mention = useDocumentMention({
		templates: mentionTemplates,
		onTemplateSelect: (id) => {
			if (id === "schema-extractor" && loanTapeEnabled) setSchemaFormOpen(true);
		},
	});
	// The host's context documents, read at send time.
	const contextDocsRef = useRef(contextDocs);
	useEffect(() => {
		contextDocsRef.current = contextDocs;
	});
	const [searchParams, setSearchParams] = useSearchParams();
	// Opening from the sidebar Templates section routes here with
	// `?template=schema-extractor`; surface the same form the @-mention does,
	// then strip the param so re-selecting the template is a real URL change
	// that re-triggers this effect (otherwise the stale param no-ops).
	// The param is stripped either way: with the flag off the link is dead, but
	// leaving `?template=` in the URL would make it look like it is still
	// pending rather than ignored.
	useEffect(() => {
		if (embedded) return;
		if (searchParams.get("template") === "schema-extractor") {
			if (loanTapeEnabled) setSchemaFormOpen(true);
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete("template");
					return next;
				},
				{ replace: true },
			);
		}
	}, [embedded, searchParams, setSearchParams, loanTapeEnabled]);
	const [input, setInput] = useState("");
	// "Ask about this" on the KPI dashboard routes here with `?prefill=<text>`:
	// the question lands in the composer as a DRAFT and nothing is sent, so the
	// user reads it and decides. Same strip-the-param shape as `template` above,
	// so asking about a second cell is a real URL change and re-fires this.
	useEffect(() => {
		if (embedded) return;
		const urlPrefill = searchParams.get("prefill");
		if (!urlPrefill) return;
		setInput(urlPrefill.slice(0, CHAT_PREFILL_MAX_LENGTH));
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("prefill");
				return next;
			},
			{ replace: true },
		);
	}, [embedded, searchParams, setSearchParams]);
	// The embedded equivalent: the host hands the question over as a prop. Keyed
	// on the nonce as well as the text so asking about a second cell with the
	// same wording still lands, and so a re-render with the same object does
	// not clobber what the user has typed since.
	const prefillText = prefill?.text ?? "";
	const prefillNonce = prefill?.nonce ?? 0;
	useEffect(() => {
		if (!prefillText) return;
		setInput(prefillText.slice(0, CHAT_PREFILL_MAX_LENGTH));
	}, [prefillText, prefillNonce]);
	// A composer draft survives an expired session (DOP-267): the 401 redirect
	// tears the page down mid-typing, so the text is mirrored into sessionStorage
	// per chat and restored when the user comes back after re-login.
	// Drafts are namespaced per user: sessionStorage outlives a logout, so an
	// unscoped key would hand the next person to sign in on this tab the
	// previous user's composer text.
	const draftKey = chatDraftKey(chatIdParam, user?.id);
	// Which chat the composer text currently belongs to. Switching chats keeps
	// this component mounted, so without the pairing the departing chat's text
	// would be written under the arriving chat's key.
	const draftSyncRef = useRef<ChatDraftSyncState>({ key: null, value: "" });
	useEffect(() => {
		const wasFirstSync = draftSyncRef.current.key === null;
		const { state, restored } = syncChatDraftForKey(
			draftSyncRef.current,
			draftKey,
			input,
		);
		draftSyncRef.current = state;
		if (restored === null) return;
		// On mount a `?prefill=` question outranks a stored draft; on a chat
		// switch the arriving chat's own draft is authoritative.
		setInput(wasFirstSync ? (current) => current || restored : restored);
	}, [input, draftKey]);
	// The chat whose submission is awaiting backend acceptance, so the pending
	// slot can be confirmed (accepted) or restored (failed, e.g. a 401).
	const pendingDraftKeyRef = useRef<string | null>(null);
	const [messages, setMessages] = useState<UIMessage[]>([]);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isStoppingStream, setIsStoppingStream] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// Which version of each question the reader chose to look at (DOP-436).
	const [selectedVersions, setSelectedVersions] = useState<SelectedVersions>({});
	// The assistant message being generated right now — the one that shows a
	// cursor. By id, because a regenerated answer streams into the slot of the
	// answer it replaces rather than at the end of the list.
	const [inFlightAssistantId, setInFlightAssistantId] = useState<string | null>(null);
	const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(
		Boolean(chatIdParam),
	);
	const [chatId, setChatId] = useState<string | null>(chatIdParam ?? null);
	const [nextCursor, setNextCursor] = useState<string | null>(null);
	const [hasMoreMessages, setHasMoreMessages] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isLoadingAllMessages, setIsLoadingAllMessages] = useState(false);
	const [chatType, setChatType] = useState<string | null>(null);
	const [chatTitle, setChatTitle] = useState<string>(""); // eslint-disable-line @typescript-eslint/no-unused-vars
	const [chatCreatedAt, setChatCreatedAt] = useState<number | undefined>(); // eslint-disable-line @typescript-eslint/no-unused-vars
	const [isTraceOpen, setIsTraceOpen] = useState(false);
	const [activeTraceMsgId, setActiveTraceMsgId] = useState<string | null>(null);
	/**
	 * The rail's body, when an answer's view has taken it (DOP-492).
	 *
	 * `variant="side-panel"` only. The rail is one narrow column with no room
	 * for a second drawer beside the thread and no business navigating away
	 * from the dashboard the reader is reading, so an answer's Audit Log or
	 * Graph opens IN PLACE of the thread and hands it back on close. The chat
	 * route keeps its drawer and its navigate; this stays null there.
	 */
	const [panelView, setPanelView] = useState<{
		kind: "audit" | "graph";
		msgId: string;
	} | null>(null);
	const isChatCreating = useRef<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const bottomRef = useRef<HTMLDivElement>(null);
	const shouldAutoScroll = useRef(true);
	const flushTimerRef = useRef<number | null>(null);
	const streamAbortControllerRef = useRef<AbortController | null>(null);
	const messagesRef = useRef<UIMessage[]>(messages);
	const pendingScrollRestoreRef = useRef<{
		prevScrollHeight: number;
		prevScrollTop: number;
	} | null>(null);
	const chatGenerationRef = useRef(0);
	const streamSeqByGenerationRef = useRef<Record<string, number>>({});
	const activeGenerationIdRef = useRef<string | null>(null);
	/** Set when this view just created a surface-tagged (rail) thread. */
	const surfaceThreadCreatedRef = useRef(false);

	const emitChatHistoryUpdated = useCallback(
		(detail?: ChatHistoryEventDetail) => {
			window.dispatchEvent(
				detail
					? new CustomEvent(CHAT_HISTORY_UPDATED_EVENT, { detail })
					: new Event(CHAT_HISTORY_UPDATED_EVENT),
			);
		},
		[],
	);

	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	useEffect(() => {
		return () => {
			if (flushTimerRef.current !== null) {
				window.clearTimeout(flushTimerRef.current);
				flushTimerRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const routeChatId = chatIdParam ?? null;
		if (routeChatId !== chatId) {
			// New-chat creation: streaming started from /chat, now URL updated to /chat/:id.
			// Just sync the chatId state without disrupting the active stream.
			// Guard against timing where `isStreaming` has not reflected yet.
			const isNewChatRouteSync =
				chatId === null &&
				routeChatId !== null &&
				(isStreaming || messages.length > 0 || isChatCreating.current);
			if (isNewChatRouteSync) {
				setChatId(routeChatId);
				return;
			}
			// Bump generation so any in-flight stream stops updating this view's state.
			// Abort the active stream on chat-route switches; otherwise it keeps running
			// against an old view generation and can never render into the restored chat.
			chatGenerationRef.current += 1;
			streamAbortControllerRef.current?.abort();
			streamAbortControllerRef.current = null;
			activeGenerationIdRef.current = null;
			if (isStreaming) {
				setIsStreaming(false);
			}
			// Follow state is per-conversation: without this, scrolling up in chat A
			// leaves the ref false, and chat B's history then renders pinned to the
			// top instead of opening on its latest message.
			shouldAutoScroll.current = true;
			const shouldClearForRouteSwitch = chatId !== null;
			if (shouldClearForRouteSwitch) {
				pendingScrollRestoreRef.current = null;
				setMessages([]);
			}
			setIsTraceOpen(false);
			setActiveTraceMsgId(null);
			setChatId(routeChatId);
			// Per-conversation, like the trace state above: keeping chat A's arm
			// would apply its rendering rules to chat B until history lands.
			setChatType(null);
			setNextCursor(null);
			setHasMoreMessages(false);
			setIsLoadingHistory(routeChatId !== null);
		}
	}, [chatIdParam, chatId, isStreaming, messages.length]);

	// The scroll container only exists once ChatContainer renders, which is gated
	// on messages.length > 0. `messages` starts empty on every mount, so effects
	// keyed on [] would read a null ref and never attach. Key them on this flag
	// instead so they bind the moment the container appears (and rebind after a
	// route switch clears the thread).
	const hasMessages = messages.length > 0;

	// Coming back from an answer's context graph: land on the paragraph the
	// reader left, not at the bottom of the thread.
	//
	// Auto-scroll-to-bottom is suppressed for this one arrival — it runs off
	// `messages` changing, which is exactly when this fires, and the two would
	// otherwise race with the bottom winning. The param is then cleared so a
	// later reply scrolls normally and a refresh does not re-jump.
	const returnToMessage = searchParams.get("message");
	useEffect(() => {
		if (!returnToMessage || !hasMessages) return;
		const container = containerRef.current;
		if (!container) return;

		const target = container.querySelector(
			`[data-message-id="${chatMessageDomId(returnToMessage)}"]`,
		);
		if (target) {
			shouldAutoScroll.current = false;
			target.scrollIntoView({ block: "center" });
		}
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("message");
				return next;
			},
			{ replace: true },
		);
	}, [returnToMessage, hasMessages, searchParams, setSearchParams]);


	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const bottomEpsilon = 32;

		// Position, not direction, decides whether we follow. scrollTop also moves
		// downward for reasons that are not the reader scrolling up — elastic
		// overscroll rebounding past the bottom, or clamping when content or the
		// viewport shrinks — and reading those as "scrolled up" would silently
		// stop following. Position also covers scrollbar drags, which emit no
		// wheel or touch events at all.
		//
		// Our own scrollTop writes need no special-casing: the pin-to-bottom
		// writes land at distance 0 (stay following), and the history-restore
		// write lands mid-thread, where not following is the correct state.
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			shouldAutoScroll.current =
				scrollHeight - scrollTop - clientHeight <= bottomEpsilon;
		};

		// Explicit upward intent detaches immediately, even when the movement is
		// too small to leave the bottom zone — otherwise nudging up a line to
		// re-read something gets stomped by the next token.
		const detachFromBottom = () => {
			shouldAutoScroll.current = false;
		};

		const handleWheel = (event: WheelEvent) => {
			if (event.deltaY < 0) detachFromBottom();
		};

		let lastTouchY: number | null = null;
		const handleTouchStart = (event: TouchEvent) => {
			lastTouchY = event.touches[0]?.clientY ?? null;
		};
		const handleTouchMove = (event: TouchEvent) => {
			const touchY = event.touches[0]?.clientY;
			if (touchY === undefined || lastTouchY === null) return;
			// Dragging the content downward scrolls the view up.
			if (touchY > lastTouchY) detachFromBottom();
			lastTouchY = touchY;
		};

		// No keydown listener: the scroll container is not focusable, so it never
		// receives key events, and a window-level listener would misfire on arrow
		// keys typed into the composer.
		container.addEventListener("scroll", handleScroll, { passive: true });
		container.addEventListener("wheel", handleWheel, { passive: true });
		container.addEventListener("touchstart", handleTouchStart, {
			passive: true,
		});
		container.addEventListener("touchmove", handleTouchMove, { passive: true });
		return () => {
			container.removeEventListener("scroll", handleScroll);
			container.removeEventListener("wheel", handleWheel);
			container.removeEventListener("touchstart", handleTouchStart);
			container.removeEventListener("touchmove", handleTouchMove);
		};
	}, [hasMessages]);

	useLayoutEffect(() => {
		const pendingScrollRestore = pendingScrollRestoreRef.current;
		const container = containerRef.current;
		if (pendingScrollRestore && container) {
			const heightDelta =
				container.scrollHeight - pendingScrollRestore.prevScrollHeight;
			container.scrollTop = pendingScrollRestore.prevScrollTop + heightDelta;
			pendingScrollRestoreRef.current = null;
			return;
		}

		if (pendingScrollRestore && !container) {
			pendingScrollRestoreRef.current = null;
		}

		if (shouldAutoScroll.current && container) {
			container.scrollTop = container.scrollHeight;
		}
	}, [messages]);

	// Re-scroll when content height changes (e.g. chart lazy-mount via
	// Suspense / IntersectionObserver after the initial scrollIntoView).
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const content = container.firstElementChild;
		if (!content) return;

		const ro = new ResizeObserver(() => {
			if (shouldAutoScroll.current) {
				container.scrollTop = container.scrollHeight;
			}
		});
		ro.observe(content);
		return () => ro.disconnect();
	}, [hasMessages]);

	const updateAssistantMessage = useCallback(
		(messageId: string, updater: (message: UIMessage) => UIMessage) => {
			setMessages((prev) =>
				prev.map((msg) => {
					if (msg.id !== messageId) return msg;
					return updater(msg);
				}),
			);
		},
		[],
	);

	const updateAssistantStreamContent = useCallback(
		(
			messageId: string,
			contentBlocks: ContentBlock[],
			generationId: string,
			lastSeq: number,
		) => {
			updateAssistantMessage(messageId, (message) => ({
				...message,
				content_blocks: contentBlocks,
				streaming:
					message.streaming?.generation_id === generationId
						? { ...message.streaming, last_seq: lastSeq }
						: message.streaming,
			}));
		},
		[updateAssistantMessage],
	);

	const clearAssistantStreaming = useCallback(
		(messageId: string, generationId: string) => {
			updateAssistantMessage(messageId, (message) => {
				if (message.streaming?.generation_id !== generationId) {
					return message;
				}
				setIsStoppingStream(false);
				return { ...message, streaming: undefined };
			});
		},
		[updateAssistantMessage],
	);

	const promoteAssistantMessageToStreaming = useCallback(
		(
			tempMessageId: string,
			assistantMessageId: number,
			generationId: string,
			initialSeq: number,
		) => {
			updateAssistantMessage(tempMessageId, (message) => ({
				...message,
				id: `history-${assistantMessageId}`,
				streaming: {
					generation_id: generationId,
					status: "pending",
					last_seq: initialSeq,
				},
			}));
			// A regenerate's placeholder version id becomes the real one, in the
			// new answer and in the versions it now sits beside (DOP-436).
			setMessages((prev) => withPendingVersionResolved(prev, assistantMessageId));
		},
		[updateAssistantMessage],
	);

	const ensureChatId = useCallback(async (): Promise<string> => {
		if (chatId) return chatId;
		if (isChatCreating.current) {
			throw new Error("Chat creation already in progress");
		}
		isChatCreating.current = true;
		try {
			// Only forward a harness the URL pinned. Omitting the field lets the
			// backend resolve it from CLAUDE_CODE_HARNESS_ENABLED per request, so
			// the arm can be flipped without shipping new frontend assets.
			const pinnedChatType = embedded ? null : searchParams.get("type");
			const createBody = embedded
				? { ...(createChatBody ?? {}) }
				: pinnedChatType
					? { chat_type: pinnedChatType }
					: {};
			const res = await apiFetch(`/api/v3/chats`, {
				method: "POST",
				headers: getApiHeaders(),
				body: JSON.stringify(createBody),
			});
			if (!res.ok) {
				console.error(`[chat] create failed: ${res.status}`);
				if (onCreateError) {
					let detail: string | null = null;
					try {
						const payload = await res.json();
						if (typeof payload?.detail === "string") detail = payload.detail;
					} catch {
						// No readable body; the status is all there is to report.
					}
					onCreateError(res.status, detail);
				}
				throw new Error(friendlyStatusMessage(res.status));
			}
			const data = await res.json();
			const newId = String(data.id);
			// The backend resolves the arm per request, so the create response is
			// the only place a brand-new chat learns which one it got. It has to
			// be known before the first turn streams: narration routing is
			// arm-scoped, and history (the other source) does not load here.
			setChatType(
				typeof data.chat_type === "string" ? data.chat_type : null,
			);
			emitChatHistoryUpdated({
				kind: "upsert",
				chat: {
					id: newId,
					title: "New Chat",
					last_message_at: null,
					created_at: Number(data.created_at),
					updated_at: Number(data.updated_at),
					has_active_stream: false,
					surface:
						typeof data.surface === "string" ? data.surface : null,
				},
			});
			if (typeof data.surface === "string") {
				surfaceThreadCreatedRef.current = true;
			}
			// Route is the source of truth for active chat id.
			// Setting local chatId before navigate can desync state from URL briefly
			// and trigger route-sync logic that detaches the first stream response.
			// Embedded, the host owns that truth: it is told the id and re-renders
			// this page with it, which the same route-sync effect then absorbs.
			if (onChatCreated) {
				onChatCreated(newId);
			} else {
				navigate(`/chat/${newId}`, { replace: true });
			}
			return newId;
		} finally {
			isChatCreating.current = false;
		}
	}, [
		chatId,
		navigate,
		searchParams,
		emitChatHistoryUpdated,
		embedded,
		createChatBody,
		onChatCreated,
		onCreateError,
	]);

	const fetchMessages = useCallback(
		async (
			targetChatId: string,
			cursor?: string | null,
			signal?: AbortSignal,
			limit: string = CHAT_HISTORY_PAGE_SIZE,
		) => {
			const params = new URLSearchParams({ limit });
			if (cursor) params.set("cursor", cursor);

			const response = await apiFetch(
				`/api/v3/chats/${targetChatId}/messages?${params.toString()}`,
				{ method: "GET", headers: getApiHeaders(), signal },
			);

			if (!response.ok) {
				console.error(`[chat] history load failed: ${response.status}`);
				throw new Error(friendlyStatusMessage(response.status));
			}

			const payload = (await response.json()) as ChatHistoryResponse;
			const contextDocIds = new Set(
				(contextDocsRef.current ?? []).map((d) => d.doc_id),
			);
			const history = Array.isArray(payload.messages) ? payload.messages : [];
			const hydrated: UIMessage[] = [...history].reverse().map((msg) => {
				const rawDocRefs = msg.message_metadata?.document_refs;
				const documentRefs: UIDocumentRef[] | undefined =
					Array.isArray(rawDocRefs) && rawDocRefs.length > 0
						? rawDocRefs
								.filter((r) => r && typeof r.doc_id === "string")
								// The host's context documents were sent, not mentioned.
								.filter((r) => !contextDocIds.has(r.doc_id))
								.map((r) => ({
									doc_id: r.doc_id,
									deal_name: r.deal_name ?? undefined,
									ref_type: r.ref_type ?? undefined,
									project_id: r.project_id ?? undefined,
									filename: r.filename ?? undefined,
								}))
						: undefined;

				return {
					id: `history-${msg.id}`,
					role: msg.role === "user" ? "user" : "assistant",
					content_blocks: Array.isArray(msg.content_blocks)
						? msg.content_blocks
						: [],
					documentRefs: msg.role === "user" ? documentRefs : undefined,
					streaming:
						msg.role !== "user" && msg.message_metadata?.streaming
							? {
									generation_id: msg.message_metadata.streaming.generation_id,
									status: msg.message_metadata.streaming.status,
									last_seq: msg.message_metadata.streaming.last_seq,
								}
							: undefined,
					evidence:
						msg.role !== "user" &&
						msg.message_metadata?.evidence &&
						typeof msg.message_metadata.evidence === "object"
							? msg.message_metadata.evidence
							: undefined,
					// A failed generation, as the backend recorded it. What
					// streamed before the failure is still shown; the flag is
					// what makes it retryable rather than final.
					failed:
						msg.role !== "user" && msg.message_metadata?.stream_error === true
							? {
									message:
										msg.message_metadata.error_message ??
										"The response stopped early.",
									generationId: msg.message_metadata.streaming?.generation_id,
								}
							: undefined,
					// Where the server places this message in the live thread, and
					// this answer's place among its question's versions (DOP-434).
					thread: msg.thread
						? {
								live: msg.thread.live !== false,
								branchOf: msg.thread.branch_of ?? undefined,
							}
						: undefined,
					versions: msg.thread?.versions
						? {
								questionMessageId: msg.thread.versions.question_message_id,
								index: msg.thread.versions.index,
								total: msg.thread.versions.total,
								ids: msg.thread.versions.ids,
							}
						: undefined,
				} satisfies UIMessage;
			});

			return {
				messages: hydrated,
				nextCursor: payload.next_cursor,
				hasMore: payload.has_more,
				chatTitle: payload.chat?.title ?? "",
				chatCreatedAt: payload.chat?.created_at,
				// The arm this chat ran on. Only the Claude harness interleaves
				// narration with tool calls, so only its messages get narration
				// routing — every other arm must render exactly as before.
				chatType: payload.chat?.chat_type ?? null,
			};
		},
		[],
	);

	/**
	 * The chat's most recent user turn, as the server has it. A failed send uses
	 * this to tell "the backend never took my message" from "it took it and the
	 * response was lost", so recovery cannot hand back a turn that already
	 * exists and invite a duplicate run (DOP-267).
	 */
	const fetchLatestUserTurnText = useCallback(
		async (targetChatId: string): Promise<string | null> => {
			const result = await fetchMessages(targetChatId, undefined, undefined, "5");
			const latestUserTurn = [...result.messages]
				.reverse()
				.find((msg) => msg.role === "user");
			if (!latestUserTurn) return null;
			return latestUserTurn.content_blocks
				.filter((block): block is TextBlock => block.type === BlockType.TEXT)
				.map((block) => block.text)
				.join("")
				.trim();
		},
		[fetchMessages],
	);

	const loadChatHistory = useCallback(
		async (targetChatId: string, signal?: AbortSignal) => {
			const result = await fetchMessages(targetChatId, undefined, signal);
			if (!result || signal?.aborted) return;
			setChatTitle(result.chatTitle);
			setChatCreatedAt(result.chatCreatedAt);
			setChatType(result.chatType);
			setMessages(result.messages);
			setNextCursor(result.nextCursor);
			setHasMoreMessages(result.hasMore);
		},
		[fetchMessages],
	);

	const loadOlderMessages = useCallback(async () => {
		if (!chatId || !nextCursor || isLoadingMore || isLoadingAllMessages) return;
		setIsLoadingMore(true);
		try {
			const result = await fetchMessages(chatId, nextCursor);
			if (!result) return;
			const container = containerRef.current;
			const prevScrollHeight = container?.scrollHeight ?? 0;
			const prevScrollTop = container?.scrollTop ?? 0;
			if (container) {
				pendingScrollRestoreRef.current = {
					prevScrollHeight,
					prevScrollTop,
				};
			}
			setMessages((prev) => [...result.messages, ...prev]);
			setNextCursor(result.nextCursor);
			setHasMoreMessages(result.hasMore);
		} catch (err) {
			console.error("Failed to load older messages:", err);
			setError(
				publicSafeErrorMessage(err, "Failed to load older messages."),
			);
		} finally {
			setIsLoadingMore(false);
		}
	}, [chatId, nextCursor, isLoadingMore, isLoadingAllMessages, fetchMessages]);

	const loadAllChatMessages = useCallback(async (): Promise<void> => {
		if (!chatId || !hasMoreMessages || isLoadingAllMessages) return;
		setIsLoadingAllMessages(true);
		try {
			const container = containerRef.current;
			const prevScrollHeight = container?.scrollHeight ?? 0;
			const prevScrollTop = container?.scrollTop ?? 0;
			let cursor: string | null = nextCursor;
			const allFetched: typeof messages = [];
			let lastCursor: string | null = null;
			let lastHasMore = true;
			while (cursor) {
				const result = await fetchMessages(chatId, cursor, undefined, "100");
				if (!result) break;
				allFetched.unshift(...result.messages);
				lastCursor = result.nextCursor;
				if (!result.hasMore) {
					lastHasMore = false;
					break;
				}
				cursor = result.nextCursor;
			}
			if (allFetched.length > 0) {
				setNextCursor(lastCursor);
				if (!lastHasMore) {
					setHasMoreMessages(false);
				}
				if (container) {
					pendingScrollRestoreRef.current = {
						prevScrollHeight,
						prevScrollTop,
					};
				}
				setMessages((prev) => [...allFetched, ...prev]);
			}
		} finally {
			setIsLoadingAllMessages(false);
		}
	}, [
		chatId,
		nextCursor,
		hasMoreMessages,
		isLoadingAllMessages,
		fetchMessages,
	]);

	useEffect(() => {
		if (!chatId || isStreaming || messages.length > 0 || !isLoadingHistory)
			return;

		const controller = new AbortController();
		setIsLoadingHistory(true);
		void (async () => {
			try {
				await loadChatHistory(chatId, controller.signal);
			} catch (err) {
				if (controller.signal.aborted) return;
				setError(publicSafeErrorMessage(err, "Failed to load chat history."));
			} finally {
				if (!controller.signal.aborted) {
					setIsLoadingHistory(false);
				}
			}
		})();

		return () => {
			controller.abort();
		};
	}, [chatId, isStreaming, isLoadingHistory, loadChatHistory, messages.length]);

	const streamChatMessage = useCallback(
		async (
			message: string,
			resolvedChatId: string,
			documentRefs?: UIDocumentRef[],
			signal?: AbortSignal,
			schemaExtractor?: SchemaExtractorPayload,
		): Promise<ChatStreamStartResponse> => {
			const response = await apiFetch(`/api/v3/chats/stream/start`, {
				method: "POST",
				headers: getApiHeaders(),
				signal,
				body: JSON.stringify({
					message,
					chat_id: resolvedChatId,
					message_metadata: {
						// The host's extras first, so the fields this page owns win.
						...(turnMetadataRef.current?.() ?? {}),
						attachments: [],
						document_refs: (documentRefs ?? []).map((d) => ({
							doc_id: d.doc_id,
							deal_name: d.deal_name,
							ref_type: d.ref_type,
							project_id: d.project_id ?? undefined,
							filename: d.filename,
						})),
						...(schemaExtractor
							? { schema_extractor: schemaExtractor }
							: {}),
					},
				}),
			});

			if (!response.ok) {
				console.error(`[chat] stream request failed: ${response.status}`);
				throw new Error(friendlyStatusMessage(response.status));
			}

			return (await response.json()) as ChatStreamStartResponse;
		},
		[],
	);

	const attachLiveStream = useCallback(
		async (
			generationId: string,
			assistantMessageId: number,
			resolvedChatId: string,
			signal?: AbortSignal,
			initialBlocks?: ContentBlock[],
			viewGeneration: number = chatGenerationRef.current,
		): Promise<void> => {
			const sinceSeq = streamSeqByGenerationRef.current[generationId] ?? 0;
			const isViewActive = () => chatGenerationRef.current === viewGeneration;
			const response = await apiFetch(
				`/api/v3/chats/stream/live?generation_id=${encodeURIComponent(generationId)}&since_seq=${sinceSeq}`,
				{
					method: "GET",
					headers: getApiHeaders(),
					signal,
				},
			);
			if (!response.ok) {
				console.error(`[chat] stream request failed: ${response.status}`);
				throw new Error(friendlyStatusMessage(response.status));
			}

			const reader = response.body?.getReader();
			if (!reader) {
				throw new Error("No response body from streaming endpoint");
			}
			const decoder = new TextDecoder("utf-8");
			const assistantMessageKey = `history-${assistantMessageId}`;
			let buffer = "";
			let currentEvent: string | null = null;
			let currentEventDataLines: string[] = [];
			let streamBlocks: ContentBlock[] | null =
				initialBlocks !== undefined ? [...initialBlocks] : null;

			const handleEnvelope = (envelope: StreamEnvelope) => {
				const lastAppliedSeq =
					streamSeqByGenerationRef.current[generationId] ?? 0;
				if (envelope.seq <= lastAppliedSeq) {
					return false;
				}

				if (!isViewActive()) {
					return (
						envelope.event === "done" ||
						envelope.event === "error" ||
						envelope.event === "cancelled"
					);
				}

				if (streamBlocks === null) {
					const existing = messagesRef.current.find(
						(msg) => msg.id === assistantMessageKey,
					);
					streamBlocks = existing?.content_blocks
						? [...existing.content_blocks]
						: [];
				}

				// The set of events that call markSeqApplied is the resume-watermark
				// contract with the backend. Keep the call sites below in sync with
				// `_rendered_stream_last_seq` in
				// backend/app/modules/chats/services/chat_service.py. Marking an
				// event applied without updating UI state (or failing to mark an
				// event whose UI effect did run) will cause reconnects to skip or
				// replay deltas across refresh.
				const markSeqApplied = () => {
					streamSeqByGenerationRef.current[generationId] = envelope.seq;
				};

				switch (envelope.event) {
						case "text_delta": {
							const text =
								typeof envelope.data.text === "string" ? envelope.data.text : "";
							const lastBlock = streamBlocks[streamBlocks.length - 1];
							if (lastBlock && lastBlock.type === BlockType.TEXT) {
								streamBlocks = [
									...streamBlocks.slice(0, -1),
									{
										type: BlockType.TEXT,
										text: lastBlock.text + text,
										ui_component_type: lastBlock.ui_component_type,
									},
								];
							} else {
								streamBlocks = [
									...streamBlocks,
									{
										type: BlockType.TEXT,
										text,
										ui_component_type: UIComponent.TEXT,
									},
								];
							}
							updateAssistantStreamContent(
								assistantMessageKey,
								streamBlocks ?? [],
								generationId,
								envelope.seq,
							);
						markSeqApplied();
						return false;
					}
					case "thinking_delta": {
						const text =
							typeof envelope.data.text === "string" ? envelope.data.text : "";
						if (!text) return false;
						const blockId =
							typeof envelope.data.block_id === "string"
								? envelope.data.block_id
								: null;
						const index = blockId
							? streamBlocks.findIndex(
									(b) =>
										b.type === BlockType.THINKING &&
										(b as { block_id?: string }).block_id === blockId,
								)
							: -1;
						if (index >= 0) {
							const existing = streamBlocks[index] as {
									type: typeof BlockType.THINKING;
									text: string;
									block_id?: string;
									turn?: number;
									ui_component_type: UIComponent;
								};
							streamBlocks = [
								...streamBlocks.slice(0, index),
								{ ...existing, text: existing.text + text },
								...streamBlocks.slice(index + 1),
							];
						} else {
							streamBlocks = [
								...streamBlocks,
									{
										type: BlockType.THINKING,
										text,
										ui_component_type: UIComponent.THINKING,
										...(blockId ? { block_id: blockId } : {}),
									},
								];
							}
							updateAssistantStreamContent(
								assistantMessageKey,
								streamBlocks ?? [],
								generationId,
								envelope.seq,
							);
						markSeqApplied();
						return false;
					}
					case "block": {
						if (
							envelope.data.type === "title" &&
							typeof envelope.data.title === "string"
						) {
							const newTitle = envelope.data.title;
							setChatTitle(newTitle);
							emitChatHistoryUpdated({
								kind: "title",
								chatId: resolvedChatId,
								title: newTitle,
							});
							markSeqApplied();
							return false;
						}
						const block = envelope.data as ContentBlock;
						streamBlocks = mergeContentBlock(streamBlocks, block);
						updateAssistantStreamContent(
							assistantMessageKey,
							streamBlocks,
							generationId,
							envelope.seq,
						);
						if (TRACE_UI_ENABLED && block.type === BlockType.TRACE) {
							setActiveTraceMsgId(assistantMessageKey);
							setIsTraceOpen(true);
						}
						markSeqApplied();
						return false;
					}
					case "evidence": {
						// Typed evidence payload (registry events + coverage
						// status) emitted live by the backend. Stash it on the
						// message so the EvidencePanel / audit count update
						// during the turn instead of waiting for a history
						// reload. Mirrors message_metadata.evidence on persist.
						const evidence = envelope.data as Record<string, unknown>;
						updateAssistantMessage(assistantMessageKey, (message) => ({
							...message,
							evidence,
						}));
						markSeqApplied();
						return false;
					}
					case "error": {
						// The envelope carries the backend's `str(exc)` verbatim — no
						// contract says a human wrote it. Allow-list, don't deny-list.
						const message = publicSafeErrorMessage(
							typeof envelope.data.message === "string"
								? envelope.data.message
								: "",
							"The response stopped early. Please try again.",
						);
						clearAssistantStreaming(assistantMessageKey, generationId);
						// The message itself carries the failure, so the Retry sits
						// on the answer it replaces and survives the banner closing.
						updateAssistantMessage(assistantMessageKey, (current) => ({
							...current,
							failed: { message, generationId },
						}));
						markSeqApplied();
						setError(message);
						return true;
					}
					case "cancelled":
					case "done":
						clearAssistantStreaming(assistantMessageKey, generationId);
						markSeqApplied();
						return true;
					default:
						return false;
				}
			};

			const processSSEEvent = (eventName: string | null, dataLines: string[]) => {
				if (eventName !== "stream" || dataLines.length === 0) return false;
				const envelope = JSON.parse(dataLines.join("\n")) as StreamEnvelope;
				return handleEnvelope(envelope);
			};

			while (true) {
				const { done, value } = await reader.read();
				const chunk = decoder.decode(value, { stream: !done });
				buffer += chunk;

				const lines = buffer.split("\n");
				buffer = lines.pop() ?? "";

				for (const line of lines) {
					if (line.startsWith("event: ")) {
						currentEvent = line.slice(7).trim();
						continue;
					}
					if (line.startsWith("data: ")) {
						currentEventDataLines.push(line.slice(6));
						continue;
					}
					if (line.trim() === "") {
						if (processSSEEvent(currentEvent, currentEventDataLines)) {
							return;
						}
						currentEvent = null;
						currentEventDataLines = [];
					}
				}

				if (done) {
					if (buffer.startsWith("data: ")) {
						currentEventDataLines.push(buffer.slice(6));
					}
					processSSEEvent(currentEvent, currentEventDataLines);
					return;
				}
				}
			},
			[
				clearAssistantStreaming,
				emitChatHistoryUpdated,
				updateAssistantStreamContent,
				updateAssistantMessage,
			],
		);

	const activeAssistantStream = useMemo(
		() => messages.find((msg) => msg.role === "assistant" && msg.streaming),
		[messages],
	);

	const activeAssistantStreamGenerationId =
		activeAssistantStream?.streaming?.generation_id ?? null;
	const activeAssistantStreamMessageId = activeAssistantStream
		? Number(activeAssistantStream.id.replace("history-", ""))
		: null;
	const activeAssistantStreamLastSeq =
		activeAssistantStream?.streaming?.last_seq ?? null;

	useEffect(() => {
		if (!chatId || isLoadingHistory) return;
		if (!activeAssistantStreamGenerationId) return;
		if (activeGenerationIdRef.current === activeAssistantStreamGenerationId) {
			return;
		}
		if (
			activeAssistantStreamMessageId === null ||
			!Number.isFinite(activeAssistantStreamMessageId)
		) {
			return;
		}

		const abortController = new AbortController();
		streamAbortControllerRef.current = abortController;
		activeGenerationIdRef.current = activeAssistantStreamGenerationId;
		setIsStreaming(true);
		const viewGeneration = chatGenerationRef.current;
		// Hydrate the per-generation seq watermark from the persisted message so
		// that the live-stream backfill resumes after the last delta written to
		// content_blocks. Without this, sinceSeq defaults to 0 and the server
		// replays already-persisted text on top of itself.
		const resumeSeq = activeAssistantStreamLastSeq;
		if (typeof resumeSeq === "number" && Number.isFinite(resumeSeq)) {
			const existing =
				streamSeqByGenerationRef.current[activeAssistantStreamGenerationId] ?? 0;
			if (resumeSeq > existing) {
				streamSeqByGenerationRef.current[activeAssistantStreamGenerationId] =
					resumeSeq;
			}
		}

		void (async () => {
			try {
				await attachLiveStream(
					activeAssistantStreamGenerationId,
					activeAssistantStreamMessageId,
					chatId,
					abortController.signal,
					undefined,
					viewGeneration,
				);
			} catch (err) {
				if (!(err instanceof DOMException && err.name === "AbortError")) {
					console.error("Failed to resume live stream:", err);
					if (chatGenerationRef.current === viewGeneration) {
						clearAssistantStreaming(
							`history-${activeAssistantStreamMessageId}`,
							activeAssistantStreamGenerationId,
						);
						setError(
							publicSafeErrorMessage(err, "Failed to resume live stream."),
						);
					}
				}
			} finally {
				if (streamAbortControllerRef.current === abortController) {
					streamAbortControllerRef.current = null;
				}
				if (activeGenerationIdRef.current === activeAssistantStreamGenerationId) {
					activeGenerationIdRef.current = null;
				}
				if (chatGenerationRef.current === viewGeneration) {
					setIsStreaming(false);
					setIsStoppingStream(false);
					// The resume path does not emit CHAT_STREAMING_STATUS_EVENT
					// (no streamToken exists for streams we didn't start in this
					// tab), so without this the sidebar indicator stays on until
					// the next unrelated history refresh. The debounced sidebar
					// fetch reconciles from has_active_stream — still running
					// streams stay flagged, completed ones clear.
					emitChatHistoryUpdated();
				}
			}
		})();

		return () => {
			abortController.abort();
		};
		// activeAssistantStreamLastSeq is intentionally excluded: changing it on
		// each streamed event must not tear down and recreate the active stream.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [
			activeAssistantStreamGenerationId,
			activeAssistantStreamMessageId,
			attachLiveStream,
			chatId,
			clearAssistantStreaming,
			emitChatHistoryUpdated,
			isLoadingHistory,
		]);

	const sendMessage = useCallback(
		async (
			message: string,
			documentRefs?: UIDocumentRef[],
			schemaExtractor?: SchemaExtractorPayload,
		) => {
			if (!message.trim()) return;

			// The host's context documents ride along on every turn, but are not
			// drawn as chips on the message: the user didn't mention them.
			const contextRefs: UIDocumentRef[] = (contextDocsRef.current ?? []).map(
				(d) => ({
					doc_id: d.doc_id,
					deal_name: d.deal_name,
					ref_type: d.ref_type === "deal" ? "deal" : "document",
					project_id: d.project_id,
					filename: d.filename,
				}),
			);
			const mentioned = new Set((documentRefs ?? []).map((d) => d.doc_id));
			const sentRefs =
				contextRefs.length > 0
					? [
							...(documentRefs ?? []),
							...contextRefs.filter((d) => !mentioned.has(d.doc_id)),
						]
					: documentRefs;

			// Keep the text recoverable until the backend accepts it — a 401 here
			// would otherwise destroy a message the user just sent (DOP-267). This
			// sits at the shared boundary so template and schema-extract runs are
			// covered too, not just the composer.
			stageChatDraftSubmission(draftKey, message);
			pendingDraftKeyRef.current = draftKey;

			setIsStreaming(true);
			setError(null);

			// Sending is an explicit request to follow the new turn, so re-arm
			// follow-the-stream even if the reader had scrolled up in the last one.
			shouldAutoScroll.current = true;

			const userMessage = createUserMessage(message, documentRefs);
			const assistantMessage = createAssistantMessage();
			setInFlightAssistantId(assistantMessage.id);
			setMessages((prev) => [...prev, userMessage, assistantMessage]);
			let resolvedChatId: string | null = null;

				const abortController = new AbortController();
				streamAbortControllerRef.current = abortController;

				// Capture the current generation so we can detect if user navigated away
				const startGeneration = chatGenerationRef.current;
				const isStillActive = () => chatGenerationRef.current === startGeneration;
				const streamToken = ++nextStreamToken;
				let ownedGenerationId: string | null = null;

			try {
				resolvedChatId = await ensureChatId();
				// The id is what the user returns to after re-login, so the
				// pending record follows it NOW — a 401 inside the stream start
				// can unmount this component before any catch block runs.
				if (pendingDraftKeyRef.current) {
					const resolvedKey = chatDraftKey(resolvedChatId, user?.id);
					migrateChatDraftSubmission(pendingDraftKeyRef.current, resolvedKey);
					pendingDraftKeyRef.current = resolvedKey;
				}
				window.dispatchEvent(
					new CustomEvent(CHAT_STREAMING_STATUS_EVENT, {
						detail: { chatId: resolvedChatId, isStreaming: true, streamToken },
					}),
				);
				const startResponse = await streamChatMessage(
					message,
					resolvedChatId,
					sentRefs,
					abortController.signal,
					schemaExtractor,
				);
				ownedGenerationId = startResponse.generation_id;
				activeGenerationIdRef.current = startResponse.generation_id;
				// A rail thread learns where it lives on its first turn: the
				// server records the view it was asked from at stream start. The
				// sidebar row inserted at create time cannot know it, and without
				// it the history link opens /chat instead of the workflow, so
				// refetch now rather than when the (minutes-long) turn ends.
				if (surfaceThreadCreatedRef.current) {
					surfaceThreadCreatedRef.current = false;
					emitChatHistoryUpdated();
				}
				// The backend owns the message now, so the recovery copy can go.
				if (pendingDraftKeyRef.current) {
					confirmChatDraftSubmission(pendingDraftKeyRef.current);
					pendingDraftKeyRef.current = null;
				}

				if (abortController.signal.aborted) {
					return;
				}

				const initialSeq = startResponse.initial_seq ?? 0;
				streamSeqByGenerationRef.current[startResponse.generation_id] = initialSeq;
				promoteAssistantMessageToStreaming(
					assistantMessage.id,
					startResponse.assistant_message_id,
					startResponse.generation_id,
					initialSeq,
				);
				setInFlightAssistantId(`history-${startResponse.assistant_message_id}`);

				await attachLiveStream(
					startResponse.generation_id,
					startResponse.assistant_message_id,
					resolvedChatId,
					abortController.signal,
					[],
					startGeneration,
				);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") {
					// User cancelled — not an error
				} else {
					console.error("Chat error:", err);
					if (isStillActive()) {
						setError(publicSafeErrorMessage(err));
					}
				}
				// The send never landed: put the text back where the user can
				// find it after re-login. `ensureChatId` may have minted a chat
				// id mid-flight, so the recovered draft is filed under the key
				// the user will actually return to as well.
				const pendingKey = pendingDraftKeyRef.current;
				if (pendingKey) {
					pendingDraftKeyRef.current = null;
					// A 401 never authenticated, so nothing was committed and the
					// text goes straight back. Any other failure is ambiguous —
					// the backend commits the turn BEFORE enqueueing and can still
					// answer 503 — so ask the server what it actually has before
					// handing the user something they may already have sent.
					const recovered = await reconcileChatDraftSubmission(pendingKey, {
						sessionExpired: isSessionExpiredError(err),
						message,
						fetchLatestUserTurn: resolvedChatId
							? () => fetchLatestUserTurnText(resolvedChatId as string)
							: undefined,
					});
					if (recovered !== null && isStillActive()) {
						setInput((current) => current || recovered);
					}
				}
			} finally {
				// Only clear shared refs if this invocation still owns them
					if (streamAbortControllerRef.current === abortController) {
						streamAbortControllerRef.current = null;
					}
					if (
						ownedGenerationId !== null &&
						activeGenerationIdRef.current === ownedGenerationId
					) {
						activeGenerationIdRef.current = null;
					}
					if (isStillActive()) {
						setIsStreaming(false);
						setIsStoppingStream(false);
						setInFlightAssistantId(null);
					}
				if (resolvedChatId) {
					if (isStillActive()) {
						try {
							await loadChatHistory(resolvedChatId);
						} catch (reloadErr) {
							console.error("Failed to refresh chat history:", reloadErr);
						}
						// Only clear the sidebar indicator when the stream actually
						// ended in this view. If `isStillActive()` is false, we were
						// aborted by a chat-route switch — the backend generation
						// keeps running, so leaving the sidebar indicator on is
						// correct. It will be reconciled the next time the user
						// returns to the chat or chat history refreshes.
						window.dispatchEvent(
							new CustomEvent(CHAT_STREAMING_STATUS_EVENT, {
								detail: {
									chatId: resolvedChatId,
									isStreaming: false,
									streamToken,
								},
							}),
						);
					}
					emitChatHistoryUpdated();
				}
			}
		},
		[
			attachLiveStream,
			draftKey,
			emitChatHistoryUpdated,
			ensureChatId,
			fetchLatestUserTurnText,
			loadChatHistory,
			promoteAssistantMessageToStreaming,
			streamChatMessage,
			user?.id,
		],
	);

	const stopStreaming = useCallback(async () => {
		if (isStoppingStream) return;
		const generationId =
			activeGenerationIdRef.current ?? activeAssistantStreamGenerationId;
		if (!generationId) {
			streamAbortControllerRef.current?.abort();
			return;
		}
		setIsStoppingStream(true);
		try {
			const response = await apiFetch(
				`/api/v3/chats/stream/${encodeURIComponent(generationId)}/cancel`,
				{
					method: "POST",
					headers: getApiHeaders(),
				},
			);
			if (!response.ok && response.status !== 404) {
				console.error(`[chat] cancel failed: ${response.status}`);
				throw new Error(friendlyStatusMessage(response.status));
			}
			setIsStoppingStream(false);
			window.setTimeout(() => {
				if (activeGenerationIdRef.current === generationId) {
					streamAbortControllerRef.current?.abort();
					if (chatId) {
						void loadChatHistory(chatId);
					}
				}
			}, STREAM_CANCEL_FALLBACK_TIMEOUT_MS);
		} catch (err) {
			console.error("Failed to cancel stream:", err);
			setIsStoppingStream(false);
			setError(publicSafeErrorMessage(err, "Failed to cancel stream."));
		}
	}, [
		activeAssistantStreamGenerationId,
		chatId,
		isStoppingStream,
		loadChatHistory,
	]);

	const handleOpenTrace = useCallback((msgId: string) => {
		if (activeTraceMsgId === msgId && isTraceOpen) {
			setIsTraceOpen(false);
		} else {
			setActiveTraceMsgId(msgId);
			setIsTraceOpen(true);
		}
	}, [activeTraceMsgId, isTraceOpen]);

	/**
	 * Show this message's Audit Log wherever this page keeps one — the drawer
	 * on the chat route, the rail's own body in a panel (DOP-492).
	 *
	 * Opens, never toggles: the callers below are not the pill. A coverage
	 * warning clicked, or a column header inspected, is a request to SEE the
	 * log, and a second inspection closing the log it just wrote to is a
	 * defect, not a toggle.
	 */
	const showAuditLog = useCallback(
		(msgId: string) => {
			setActiveTraceMsgId(msgId);
			if (variant === "side-panel") setPanelView({ kind: "audit", msgId });
			else setIsTraceOpen(true);
		},
		[variant],
	);

	// Evidence and trace are surfaced in one panel ("Audit log"); both
	// entry buttons toggle the same trace state.
	const handleOpenEvidence = useCallback(
		(msgId: string) => {
			if (variant !== "side-panel" && activeTraceMsgId === msgId && isTraceOpen) {
				setIsTraceOpen(false);
				return;
			}
			showAuditLog(msgId);
		},
		[activeTraceMsgId, isTraceOpen, showAuditLog, variant],
	);

	/**
	 * The rail's Audit pill: swap the thread for this answer's log, or put the
	 * thread back when the same answer's log is already up. Toggling matters
	 * more here than in the drawer — the pill is the ONLY way back to a thread
	 * the log has replaced, apart from the header's own control.
	 */
	const handleOpenPanelTrace = useCallback((msgId: string) => {
		setActiveTraceMsgId(msgId);
		setPanelView((prev) =>
			prev && prev.kind === "audit" && prev.msgId === msgId
				? null
				: { kind: "audit", msgId },
		);
	}, []);

	/** The rail's Graph pill — the same swap, with the graph in the body. */
	const handleOpenPanelGraph = useCallback((msgId: string) => {
		setPanelView((prev) =>
			prev && prev.kind === "graph" && prev.msgId === msgId
				? null
				: { kind: "graph", msgId },
		);
	}, []);

	const closePanelView = useCallback(() => setPanelView(null), []);

	// A view outlives its answer only as a blank: switching threads from the
	// rail's history menu, or starting a new one, leaves the open log pointing
	// at a message that is no longer in the thread. Hand the rail back.
	useEffect(() => {
		if (!panelView) return;
		if (!messages.some((m) => m.id === panelView.msgId)) setPanelView(null);
	}, [messages, panelView]);

	// Column-lineage inspections, keyed by message id. Session-only: an
	// accumulating series (one entry per header click, never replaced) that
	// feeds the Audit Log panel. Cleared on reload by design.
	const [columnInspections, setColumnInspections] = useState<
		Record<string, ColumnInspection[]>
	>({});
	const handleInspectColumn = useCallback(
		(msgId: string, column: LoanTapePreviewColumn) => {
			setColumnInspections((prev) => {
				const list = prev[msgId] ?? [];
				const seq = list.length + 1;
				return {
					...prev,
					[msgId]: [
						...list,
						{ ...column, id: `insp-${msgId}-${seq}`, seq },
					],
				};
			});
			// Surface the Audit Log for this message so the series is visible as
			// it accumulates — in the rail that means the in-rail view, which is
			// the only Audit Log a panel has (DOP-492).
			showAuditLog(msgId);
		},
		[showAuditLog],
	);
	const inspectionCounts = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const [msgId, list] of Object.entries(columnInspections)) {
			counts[msgId] = list.length;
		}
		return counts;
	}, [columnInspections]);
	const activeInspections = useMemo(
		() => (activeTraceMsgId ? columnInspections[activeTraceMsgId] ?? [] : []),
		[activeTraceMsgId, columnInspections],
	);

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		const cursorPos = e.target.selectionStart ?? value.length;
		const result = mention.handleInputChange(value, cursorPos);
		setInput(result);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (input.trim() && !isStreaming) {
			const message = input;
			const docRefs =
				mention.selectedDocs.length > 0
					? mention.selectedDocs.map((d) => ({
							doc_id: d.doc_id,
							deal_name: d.deal_name,
							ref_type: d.ref_type as "document" | "deal" | undefined,
							project_id: d.project_id,
							filename: d.filename,
						}))
					: undefined;
			setInput("");
			sendMessage(message, docRefs);
			mention.clearSelection();
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		const mentionResult = mention.handleKeyDown(
			e as React.KeyboardEvent<HTMLTextAreaElement>,
			input,
		);
		if (mentionResult !== false) {
			if (typeof mentionResult === "string") {
				setInput(mentionResult);
			}
			return;
		}
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	const handleMentionSelect = useCallback(
		(doc: Parameters<typeof mention.selectDocument>[0]) => {
			const cleaned = mention.selectDocument(doc, input);
			if (cleaned !== undefined) {
				setInput(cleaned);
			}
		},
		[mention, input],
	);

	/**
	 * Regenerate an answer in place (DOP-434/436): the same question, answered
	 * again as a further version. Nothing is re-sent as a user message; the
	 * new answer streams into the old one's slot, and the old one stays
	 * readable through the version switcher. Works on a finished answer as
	 * much as on a failed one.
	 */
	const regenerateMessage = useCallback(
		async (assistantId: string) => {
			if (isStreaming || !chatId) return;
			const messageId = persistedMessageId(assistantId);
			if (messageId === undefined) {
				setError("This answer is still being written; wait for it to finish.");
				return;
			}
			const questionId = messages.find((m) => m.id === assistantId)?.versions
				?.questionMessageId;

			setIsStreaming(true);
			setError(null);
			shouldAutoScroll.current = true;

			const placeholder = createAssistantMessage();
			setInFlightAssistantId(placeholder.id);
			setMessages((prev) =>
				withLocalRegeneration(prev, assistantId, placeholder),
			);
			// The newest version is the live one; a regenerate always lands
			// there, so any older selection on this question is dropped.
			if (questionId !== undefined) {
				setSelectedVersions((prev) => {
					if (!(questionId in prev)) return prev;
					const next = { ...prev };
					delete next[questionId];
					return next;
				});
			}

			const abortController = new AbortController();
			streamAbortControllerRef.current = abortController;
			const startGeneration = chatGenerationRef.current;
			const isStillActive = () => chatGenerationRef.current === startGeneration;
			const streamToken = ++nextStreamToken;
			let ownedGenerationId: string | null = null;

			try {
				window.dispatchEvent(
					new CustomEvent(CHAT_STREAMING_STATUS_EVENT, {
						detail: { chatId, isStreaming: true, streamToken },
					}),
				);
				const response = await apiFetch(`/api/v3/chats/stream/regenerate`, {
					method: "POST",
					headers: getApiHeaders(),
					signal: abortController.signal,
					body: JSON.stringify({ chat_id: chatId, message_id: messageId }),
				});
				if (!response.ok) {
					console.error(`[chat] regenerate request failed: ${response.status}`);
					throw new Error(friendlyStatusMessage(response.status));
				}
				const startResponse =
					(await response.json()) as ChatStreamStartResponse;
				ownedGenerationId = startResponse.generation_id;
				activeGenerationIdRef.current = startResponse.generation_id;
				if (abortController.signal.aborted) return;

				const initialSeq = startResponse.initial_seq ?? 0;
				streamSeqByGenerationRef.current[startResponse.generation_id] =
					initialSeq;
				promoteAssistantMessageToStreaming(
					placeholder.id,
					startResponse.assistant_message_id,
					startResponse.generation_id,
					initialSeq,
				);
				setInFlightAssistantId(`history-${startResponse.assistant_message_id}`);

				await attachLiveStream(
					startResponse.generation_id,
					startResponse.assistant_message_id,
					chatId,
					abortController.signal,
					[],
					startGeneration,
				);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") {
					// User cancelled — not an error
				} else {
					console.error("Regenerate error:", err);
					if (isStillActive()) {
						setError(publicSafeErrorMessage(err));
					}
				}
			} finally {
				if (streamAbortControllerRef.current === abortController) {
					streamAbortControllerRef.current = null;
				}
				if (
					ownedGenerationId !== null &&
					activeGenerationIdRef.current === ownedGenerationId
				) {
					activeGenerationIdRef.current = null;
				}
				if (isStillActive()) {
					setIsStreaming(false);
					setIsStoppingStream(false);
					setInFlightAssistantId(null);
					// The server's view of the thread replaces the local guess.
					try {
						await loadChatHistory(chatId);
					} catch (reloadErr) {
						console.error("Failed to refresh chat history:", reloadErr);
					}
					window.dispatchEvent(
						new CustomEvent(CHAT_STREAMING_STATUS_EVENT, {
							detail: { chatId, isStreaming: false, streamToken },
						}),
					);
				}
				emitChatHistoryUpdated();
			}
		},
		[
			attachLiveStream,
			chatId,
			emitChatHistoryUpdated,
			isStreaming,
			loadChatHistory,
			messages,
			promoteAssistantMessageToStreaming,
		],
	);

	const handleSelectVersion = useCallback(
		async (questionMessageId: number, versionMessageId: number) => {
			// The version ids come from the whole chat; the page holds only what
			// has been fetched. An older version on an unfetched page is loaded
			// first, so the selection has something to show.
			const isLoaded = messages.some(
				(m) => persistedMessageId(m.id) === versionMessageId,
			);
			if (!isLoaded && hasMoreMessages) {
				await loadAllChatMessages();
			}
			setSelectedVersions((prev) => ({
				...prev,
				[questionMessageId]: versionMessageId,
			}));
		},
		[hasMoreMessages, loadAllChatMessages, messages],
	);

	const handleSchemaExtractSubmit = useCallback(
		(payload: SchemaExtractorPayload) => {
			setSchemaFormOpen(false);
			const stem = (f?: string) => (f ? f.replace(/\.[^.]+$/, "") : "");
			const newSource = payload.new_source.filename ?? "the selected workbook";
			const rawData = payload.training_source.filename ?? "the raw data file";
			const trainingTarget =
				payload.training_target.filename ?? "the training loan tape";
			const newTarget =
				payload.output_filename?.trim() ||
				(payload.new_source.filename
					? `${stem(payload.new_source.filename)} - Loan Tape.xlsx`
					: "the generated loan tape");
			const message = [
				`Run **Loan Tape Extraction** on **${newSource}**`,
				"",
				`- Map extraction and conversion of raw data into clean loan tape from **${rawData}** to **${trainingTarget}**`,
				`- Apply the mapped conversion to **${newTarget}**`,
			].join("\n");
			sendMessage(message, undefined, payload);
		},
		[sendMessage],
	);

	// The Templates page composer hands its payload over via router state and
	// lands here on a fresh chat; consume it once and fire the run so the user
	// arrives watching the extraction stream, not staring at an empty composer.
	// A ref guards the send (StrictMode re-runs effects), and the state is
	// stripped with a replace-navigation so refresh/back cannot re-submit.
	const location = useLocation();
	const consumedLoanTapeRef = useRef(false);
	useEffect(() => {
		const payload = (
			location.state as { loanTapePayload?: SchemaExtractorPayload } | null
		)?.loanTapePayload;
		if (!payload || consumedLoanTapeRef.current) return;
		consumedLoanTapeRef.current = true;
		navigate(location.pathname, { replace: true, state: null });
		handleSchemaExtractSubmit(payload);
	}, [location, navigate, handleSchemaExtractSubmit]);

	const activeTraceBlocks = useMemo((): TraceBlock[] => {
		// Trace steps are internal observability. When TRACE_UI is off the
		// audit panel must show evidence only — even for history-loaded
		// messages whose content_blocks still carry persisted trace blocks.
		if (!TRACE_UI_ENABLED) return [];
		const msg = activeTraceMsgId
			? messages.find((m) => m.id === activeTraceMsgId)
			: null;
		if (!msg) return [];
		return msg.content_blocks.filter(
			(b): b is TraceBlock => b.type === BlockType.TRACE,
		);
	}, [messages, activeTraceMsgId]);

	// The audit timeline classifies narration by position, so it needs the
	// active message's blocks in full — trace blocks alone lose the ordering
	// that makes a text block narration rather than the answer.
	const activeMessage = useMemo(
		() =>
			activeTraceMsgId
				? (messages.find((m) => m.id === activeTraceMsgId) ?? null)
				: null,
		[messages, activeTraceMsgId],
	);

	// Parse evidence events from the active message. Prefer the persisted
	// metadata payload (set by chat_service on message flush); fall back to
	// the in-band EVIDENCE_METADATA trace block emitted live during the
	// stream. Either source goes through Zod so a malformed event is
	// dropped rather than blanking the whole panel.
	const activeEvidenceEvents = useMemo((): EvidenceEvent[] => {
		if (!TRACE_UI_ENABLED) return [];
		const msg = activeTraceMsgId
			? messages.find((m) => m.id === activeTraceMsgId)
			: null;
		if (!msg) return [];
		let raw: unknown = msg.evidence?.events;
		if (!Array.isArray(raw)) {
			const evidenceBlock = msg.content_blocks.find(
				(b): b is TraceBlock =>
					b.type === BlockType.TRACE &&
					b.trace_type === TraceType.EVIDENCE_METADATA,
			);
			raw = evidenceBlock?.evidence?.events;
		}
		if (!Array.isArray(raw)) return [];
		const parsed: EvidenceEvent[] = [];
		for (const item of raw) {
			const result = EvidenceEventSchema.safeParse(item);
			if (result.success) parsed.push(result.data);
		}
		return parsed;
	}, [messages, activeTraceMsgId]);

	/**
	 * The question an in-rail view belongs to, trimmed to a line. Read from the
	 * nearest user message ABOVE the answer, which is what the reader asked —
	 * the answer's own first words would restate the model, not the question.
	 */
	const panelViewAboutLabel = useMemo((): string | undefined => {
		if (!panelView) return undefined;
		const index = messages.findIndex((m) => m.id === panelView.msgId);
		if (index < 0) return undefined;
		for (let i = index; i >= 0; i--) {
			const msg = messages[i];
			if (msg.role !== "user") continue;
			const text = msg.content_blocks
				.filter((b): b is TextBlock => b.type === BlockType.TEXT)
				.map((b) => b.text)
				.join(" ")
				.trim();
			if (!text) return undefined;
			return text.length > 60 ? `${text.slice(0, 60).trimEnd()}…` : text;
		}
		return undefined;
	}, [messages, panelView]);

	const mentionDropdown = (
		<MentionDropdown
			results={mention.mentionResults}
			highlightedIndex={mention.highlightedIndex}
			onSelect={handleMentionSelect}
			visible={mention.isMentionActive}
			loading={mention.isMentionLoading}
			onDismiss={mention.dismissMention}
		/>
	);

	return (
		<SpreadsheetViewerProvider>
			<PdfViewerProvider>
		{/* The chain is scoped to the answer being audited: opening a different
		    message's trail starts a fresh chain, so a card can never outlive the
		    answer whose phrase summoned it. */}
		<CitationChainProvider
			messageKey={activeTraceMsgId}
			enabled={isAuditTimelineEnabled()}
		>
		<SchemaExtractorForm
			open={schemaFormOpen}
			onClose={() => setSchemaFormOpen(false)}
			onSubmit={handleSchemaExtractSubmit}
		/>
		<div
			className={`chat-container ${
				variant === "side-panel" ? "chat-side-panel" : ""
			}`}
		>
			<div
				className={`chat-content-row${isTraceOpen && TRACE_UI_ENABLED ? " trace-open" : ""}`}
			>
				<div className="chat-main-area">
					<ErrorDisplay
						error={error ? new Error(error) : undefined}
						onRetry={() => setError(null)}
					/>

					{variant !== "side-panel" && (
					<div
						className="flex items-center gap-2"
						style={{ alignSelf: "flex-end", padding: "16px 16px 0 0" }}
					>
						<ChatExportDropdown
							chatId={chatId}
							chatTitle={chatTitle}
							chatCreatedAt={chatCreatedAt}
							containerRef={containerRef}
							messages={messages}
							hasMoreMessages={hasMoreMessages}
							isStreaming={isStreaming}
							isLoadingHistory={isLoadingHistory}
							isLoadingAllMessages={isLoadingAllMessages}
							onLoadAllMessages={loadAllChatMessages}
						/>

						{/* <ShareChat chatId={chatId} /> */}
					</div>
					)}

					{messages.length === 0 && variant !== "side-panel" ? (
						isLoadingHistory ? (
							<div className="chat-history-loading">
								<div className="chat-history-loading-spinner" />
								<div className="chat-history-loading-text">
									Loading conversation...
								</div>
							</div>
						) : (
							<HeroSection
								input={input}
								isLoading={isStreaming}
								onInputChange={handleInputChange}
								onSubmit={handleSubmit}
								onKeyPress={handleKeyPress}
								onSuggestionClick={(msg: string) => {
									const refs =
										mention.selectedDocs.length > 0
											? mention.selectedDocs.map((d) => ({
													doc_id: d.doc_id,
													deal_name: d.deal_name,
													ref_type: d.ref_type as
														| "document"
														| "deal"
														| undefined,
													project_id: d.project_id,
													filename: d.filename,
												}))
											: undefined;
									sendMessage(msg, refs);
									mention.clearSelection();
								}}
								variant={variant}
								selectedDocs={mention.selectedDocs}
								onDocRemove={mention.removeDocument}
								mentionDropdown={mentionDropdown}
							/>
						)
					) : (
						<ChatContainer
							containerRef={containerRef}
							bottomRef={bottomRef}
							messages={messages}
							isLoading={isStreaming}
							isStopping={isStoppingStream}
							input={input}
							onInputChange={handleInputChange}
							onSubmit={handleSubmit}
							onKeyPress={handleKeyPress}
							onStopStreaming={stopStreaming}
							onRegenerateMessage={regenerateMessage}
							streamingMessageId={
								inFlightAssistantId ?? activeAssistantStream?.id ?? null
							}
							selectedVersions={selectedVersions}
							onSelectVersion={handleSelectVersion}
							variant={variant}
							hasMoreMessages={hasMoreMessages}
							isLoadingMore={isLoadingMore}
							isLoadingAll={isLoadingAllMessages}
							onLoadMore={loadOlderMessages}
							chatId={chatId}
							chatType={chatType}
							selectedDocs={mention.selectedDocs}
							onDocRemove={mention.removeDocument}
							mentionDropdown={mentionDropdown}
							// A panel has no drawer to slide out, so its pills swap the
							// rail's own body instead (DOP-492); the route keeps the
							// drawer and the navigate to /context-graph.
							onOpenTrace={
								variant === "side-panel"
									? handleOpenPanelTrace
									: handleOpenTrace
							}
							onOpenEvidence={handleOpenEvidence}
							onOpenGraph={
								variant === "side-panel" ? handleOpenPanelGraph : undefined
							}
							openView={variant === "side-panel" ? panelView : null}
							inspectionCounts={inspectionCounts}
							onInspectColumn={handleInspectColumn}
							// A panel keeps the thread's shape from the first turn: no
							// landing-page hero, the composer docked below one line
							// of guidance (or the loading note while history arrives).
							{...(variant === "side-panel"
								? {
										emptyState: isLoadingHistory
											? "Loading conversation…"
											: sidePanelEmptyPrompt,
										placeholder: "Ask about what's on screen",
									}
								: {})}
						/>
					)}
					{panelView && (
						// Over the thread, not instead of it: the thread stays
						// mounted so its scroll position — and a stream still
						// writing into it — survive the trip out and back.
						<div className="rail-view">
							{panelView.kind === "audit" ? (
								<TracePanel
									variant="embedded"
									aboutLabel={panelViewAboutLabel}
									blocks={activeTraceBlocks}
									contentBlocks={activeMessage?.content_blocks}
									documentRefs={activeMessage?.documentRefs}
									chatType={chatType}
									events={activeEvidenceEvents}
									inspections={activeInspections}
									chatId={chatId}
									isStreaming={
										isStreaming &&
										activeTraceMsgId === messages[messages.length - 1]?.id
									}
									isOpen
									onClose={closePanelView}
								/>
							) : (
								<RailGraphView
									chatId={chatId ?? ""}
									messageDbId={persistedMessageDbId(panelView.msgId) ?? ""}
									aboutLabel={panelViewAboutLabel}
									onBack={closePanelView}
								/>
							)}
						</div>
					)}
				</div>
				{TRACE_UI_ENABLED && variant !== "side-panel" && (
					<TracePanel
						blocks={activeTraceBlocks}
						contentBlocks={activeMessage?.content_blocks}
						documentRefs={activeMessage?.documentRefs}
						chatType={chatType}
						events={activeEvidenceEvents}
						inspections={activeInspections}
						chatId={chatId}
						isStreaming={isStreaming && activeTraceMsgId === messages[messages.length - 1]?.id}
						isOpen={isTraceOpen}
						onClose={() => setIsTraceOpen(false)}
					/>
				)}
			</div>
		</div>
		</CitationChainProvider>
			</PdfViewerProvider>
		</SpreadsheetViewerProvider>
	);
};

export default Chat;
